<script>
import Vue from 'vue'
import { GlobalBus } from '@services/bus'
import { MODAL } from '@constants/Text'
import { forceReload } from '@utils/chunkErrorHandler'

export const AUTO_RELOAD_DELAY_MS = 5000

/**
 * Modal "Nova versão disponível" — exibido em dois cenários:
 *
 * 1. Reativo (legado, AAWZ-WEB-3VT): chunkErrorHandler captura um
 *    ChunkLoadError e emite o evento com { autoReload: true }. O modal
 *    agenda um forceReload() em 2s — comportamento original.
 *
 * 2. Proativo (heartbeat / visibilitychange): version-check.js detecta
 *    versão nova no backend e emite com { autoReload: false }. O modal
 *    NÃO agenda reload — o usuário decide. Se ele dispensar, emitimos
 *    `version-check:dismissed` para a flag in-memory bloquear próximos
 *    disparos proativos na mesma sessão. ChunkLoadError continua
 *    disparando o modal mesmo após dismissal.
 *
 * Anti-loop: se forceReload detectar N reloads em 30s, mostra estado
 * alternativo pedindo Ctrl+Shift+R.
 *
 * forceReload acrescenta ?_v=<timestamp> à URL para forçar cache-miss
 * (window.location.reload() simples respeitaria o cache do navegador e
 * traria o mesmo index.html antigo → loop infinito de modal).
 */
export default {
  name: 'TheNewVersionModal',
  data() {
    return {
      busModal: new Vue(),
      reloadTimer: null,
      loopDetected: false,
      autoReloadActive: false,
    }
  },
  created() {
    GlobalBus.$on(MODAL.NEW_VERSION_AVAILABLE, this.open)
  },
  beforeDestroy() {
    GlobalBus.$off(MODAL.NEW_VERSION_AVAILABLE, this.open)
    this.cancelScheduledReload()
  },
  methods: {
    open(payload) {
      this.loopDetected = false
      const autoReload = !payload || payload.autoReload !== false
      this.autoReloadActive = autoReload
      this.busModal.$emit('dialog', true)
      if (autoReload) {
        this.scheduleReload()
      } else {
        // Cancela timer pendente — protege o caso em que o modal abre
        // primeiro reativo (autoReload=true → agenda 2s) e ANTES do timer
        // disparar chega um trigger proativo (autoReload=false). Sem este
        // cancel, performReload() ainda executaria após 2s mesmo no modo
        // manual.
        this.cancelScheduledReload()
      }
    },
    scheduleReload() {
      if (this.reloadTimer) return
      this.reloadTimer = setTimeout(() => {
        this.reloadTimer = null
        this.performReload()
      }, AUTO_RELOAD_DELAY_MS)
    },
    cancelScheduledReload() {
      if (!this.reloadTimer) return
      clearTimeout(this.reloadTimer)
      this.reloadTimer = null
    },
    performReload() {
      const result = forceReload()
      if (!result.reloaded && result.reason === 'loop') {
        this.loopDetected = true
      }
    },
    reload() {
      this.cancelScheduledReload()
      this.performReload()
    },
    dismiss() {
      this.cancelScheduledReload()
      this.busModal.$emit('dialog', false)
      GlobalBus.$emit(MODAL.NEW_VERSION_DISMISSED)
    },
  },
}
</script>

<template>
  <BaseModal
    :bus="busModal"
    centerScreen
    persistent
    withoutCloseButton
    maxWidth="400px"
  >
    <template v-slot:title>
      <span class="text-heading-small ml-4 mt-2">Nova versão disponível</span>
    </template>
    <div class="pa-4">
      <p v-if="loopDetected" class="ma-0">
        Não conseguimos atualizar automaticamente. Pressione
        <strong>Ctrl+Shift+R</strong> (ou <strong>Cmd+Shift+R</strong> no Mac)
        para recarregar limpando o cache do navegador.
      </p>
      <p v-else-if="autoReloadActive" class="ma-0">
        Uma nova versão da AAWZ está disponível. A página será atualizada
        automaticamente em alguns segundos.
      </p>
      <p v-else class="ma-0">
        Uma nova versão da AAWZ está disponível. Atualize quando quiser para
        carregar a versão nova.
      </p>
    </div>
    <template v-slot:actions>
      <BaseButton
        v-if="!loopDetected && !autoReloadActive"
        underlined
        @click="dismiss"
      >
        Lembrar mais tarde
      </BaseButton>
      <BaseButton v-if="!loopDetected" primary @click="reload">
        Atualizar agora
      </BaseButton>
    </template>
  </BaseModal>
</template>
